import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AsyncSelect from 'react-select/async'
import styled from 'styled-components'
import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import Handle from '~/prix/react/components/handle'
import Modal from '~/prix/react/components/modal'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import useAPI from '~/prix/react/hooks/api'
import geoListDefinition from '../geo/list/geoList.definition'
import {
  childrenItemViewLabels,
  compatibleSpaceChildren,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '~/packages/general/variable/spaceTime.data'
import {
  Layer,
  Space,
  SpaceChildrenGeoStandard,
  SpaceChildrenItem,
  SpaceGeoStandardId,
  SpaceGeoStandardType,
  SpaceType,
} from '~/packages/general/variable/spaceTime.types'
import debounce from 'debounce-promise'
import DemarcationIcon from '~/components/icons/design/64px_draw-polygon.svg'
import ModalSpaceDemarcation, { FeaturePolygonWithHash } from './modalSpaceDemarcation.component'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import { colors } from '~/design'
import { isVariableLayer } from './clusterListInput.util'

type GeoOption = {
  id: SpaceGeoStandardId
  name: string
  subtitle?: string
  type: SpaceGeoStandardType
  searchRanking?: number | null
}

const geoStandardTypes: SpaceGeoStandardType[] = [
  'community',
  'neighborhood',
  'district',
  'street',
  'municipality',
  'microRegion',
  'mesoRegion',
  'state',
  'macroRegion',
  'country',
]

function isSpaceGeoStandardType(type: string | null | undefined): type is SpaceGeoStandardType {
  return !!type && geoStandardTypes.includes(type as SpaceGeoStandardType)
}

function isGeoOption(item: {
  id: SpaceGeoStandardId
  name?: string | null
  subtitle?: string | null
  type?: string | null
  searchRanking?: number | null
}): item is GeoOption {
  return typeof item.name === 'string' && isSpaceGeoStandardType(item.type)
}

function removeItemSpaceFields(space: Space) {
  const {
    childrenItemView: _childrenItemView,
    childrenH3Resolution: _childrenH3Resolution,
    ...spaceWithoutItemFields
  } = space as Space & Partial<SpaceChildrenItem>

  return spaceWithoutItemFields
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-height: 70vh;

  .top {
    display: flex;
    flex-direction: row;
    padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(1)};
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px 8px 0 0;

    .input-wrapper {
      position: relative;
      border-radius: 8px 8px 0 0;
      width: 95%;
      padding-right: 5px;

      @media (max-width: 575px) {
        width: 90%;
      }

      @media (max-width: 400px) {
        width: 85%;
      }
    }

    .button-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      border-top-right-radius: 8px;
      position: relative;

      button {
        height: 40px;
        width: 40px;
        background-color: #fff;
        border-style: none;
        cursor: pointer;
        justify-content: center;
        align-items: center;
        border: 1px solid #cccccc;
        border-radius: 8px;
        box-shadow: none;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: border-color 0.3s ease-in-out;

        &:hover {
          border: 1px solid #adadad;
        }
      }
    }
  }

  .children-options {
    flex: 1;

    overflow-x: hidden;
    overflow-y: scroll;

    .children-option {
      display: flex;
      position: relative;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      cursor: pointer;

      &:not(:last-child)::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        margin-left: 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
        width: 100%;
      }

      &:hover {
        background-color: ${({ theme }) => theme.colors.lightGrey};

        &:last-child {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }
      }

      .title {
        display: flex;
        align-items: center;
        font-weight: bold;
        gap: 4px;
      }

      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        flex-direction: column;
        align-items: flex-start;

        .right {
          margin-top: 1rem;
          justify-content: flex-start;
        }
      }
    }

    .non-clickable {
      display: flex;
      place-content: center;
      padding: 2rem;

      .title {
        display: flex;
        align-items: center;
        font-weight: bold;
        gap: 4px;
      }
    }
  }
`

interface ViewSelectModalSpaceProps {
  layer: Layer
  isOpen: boolean
  onClose: () => void
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
}

export default function ViewSelectModalSpace({
  layer,
  isOpen,
  onClose,
  onChangeLayer,
}: ViewSelectModalSpaceProps) {
  if (!isVariableLayer(layer)) {
    return <output>Seleção geográfica indisponível para essa camada.</output>
  }

  const { context } = useAPI()

  const [currentLayer, setCurrentLayer] = useState<typeof layer>(layer)
  const [polygonDemarcation, setPolygonDemarcation] = useState<FeaturePolygonWithHash | null>(null)
  const [isOpenDemarcationModal, setIsOpenDemarcationModal] = useState<boolean>(false)

  useEffect(() => {
    setCurrentLayer(layer)
  }, [layer])

  const { callAction } = useActionFunction(geoListDefinition)

  const coerceChildrenType = (_targetLayer: Layer, requestedType: SpaceType): SpaceType =>
    requestedType

  const shouldDisplayChildrenOption = (_targetLayer: Layer, _optionType: SpaceType) => true

  const handleSelectChildrenOption = (key: Exclude<SpaceType, 'custom'>) => {
    if (!context.user) {
      return
    }

    setCurrentLayer(oldLayer => {
      const coercedType = coerceChildrenType(oldLayer, key)
      const isSameAsDelimitation = coercedType === oldLayer.space.delimitationType

      const baseSpace = removeItemSpaceFields(oldLayer.space)
      const nextChildrenType = coercedType as Exclude<SpaceType, 'item' | 'custom'>
      const space: Space = {
        ...baseSpace,
        childrenType: nextChildrenType,
        childrenTitle: isSameAsDelimitation
          ? spaceTypeSingularLabels[coercedType]
          : spaceTypePluralLabels[coercedType],
      }

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const handleSelectChildrenViewOption = (key: 'item', childrenItemViewKey: string) => {
    if (!context.user) {
      return
    }

    setCurrentLayer(oldLayer => {
      const coercedType = coerceChildrenType(oldLayer, key)

      if (coercedType !== 'item') {
        const baseSpace = removeItemSpaceFields(oldLayer.space)
        const nextChildrenType = coercedType as Exclude<SpaceType, 'item' | 'custom'>
        const space: Space = {
          ...baseSpace,
          childrenType: nextChildrenType,
          childrenTitle:
            coercedType === oldLayer.space.delimitationType
              ? spaceTypeSingularLabels[coercedType]
              : spaceTypePluralLabels[coercedType],
        }

        return {
          ...oldLayer,
          space,
        }
      }

      const space = {
        ...removeItemSpaceFields(oldLayer.space),
        childrenType: key,
        childrenItemView: childrenItemViewKey as SpaceChildrenItem['childrenItemView'],
        childrenH3Resolution: undefined,
      } as Space

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const handleSetDelimitationFromSelect = (
    item: {
      id: SpaceGeoStandardId
      name: string
      subtitle?: string
      type: SpaceType
    } | null,
  ) => {
    if (!item || !context.user || !isSpaceGeoStandardType(item.type)) {
      return
    }

    const delimitationType = item.type
    setPolygonDemarcation(null)
    setCurrentLayer(oldLayer => {
      const compatibleChildrenForDelimitation =
        compatibleSpaceChildren[delimitationType as keyof typeof compatibleSpaceChildren] ?? []
      const newCompatibleChildren = layer.variable.compatibleChildrenTypes
        ? compatibleChildrenForDelimitation.filter(type =>
            layer.variable.compatibleChildrenTypes!.includes(type),
          )
        : compatibleChildrenForDelimitation

      let childrenType: SpaceType = oldLayer.space.childrenType
      let childrenTitle = (oldLayer.space as SpaceChildrenGeoStandard).childrenTitle
      let childrenItemView = (oldLayer.space as SpaceChildrenItem).childrenItemView
      if (!newCompatibleChildren.includes(childrenType)) {
        childrenType = newCompatibleChildren[0] as SpaceType

        if (childrenType === 'item') {
          childrenItemView = 'heatmap'
          childrenTitle = childrenItemViewLabels[childrenItemView]
        } else {
          childrenTitle = spaceTypePluralLabels[childrenType]
        }
      }

      if (delimitationType === 'country' && childrenType === 'item' && childrenItemView === 'cluster') {
        childrenItemView = 'heatmap'
        childrenTitle = childrenItemViewLabels[childrenItemView]
      }

      const baseSpace = removeItemSpaceFields(oldLayer.space)
      const space: Space =
        childrenType === 'item'
          ? {
              ...baseSpace,
              childrenTitle,
              childrenType: 'item',
              childrenItemView,
              childrenH3Resolution: undefined,
              delimitationId: item.id,
              delimitationTitle: item.name,
              delimitationType,
            }
          : {
              ...baseSpace,
              childrenTitle,
              childrenType: childrenType as Exclude<SpaceType, 'item' | 'custom'>,
              delimitationId: item.id,
              delimitationTitle: item.name,
              delimitationType,
            }

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const handleSetDelimitationFromCustomPolygon = (
    newPolygonDemarcation: FeaturePolygonWithHash | null,
  ) => {
    setPolygonDemarcation(newPolygonDemarcation)
    if (!newPolygonDemarcation) {
      return
    }

    setCurrentLayer(oldLayer => {
      let childrenType: SpaceType = 'item'
      let childrenItemView: 'cluster' | 'heatmap' =
        (oldLayer.space as SpaceChildrenItem).childrenItemView === 'cluster' ? 'cluster' : 'heatmap'
      let childrenTitle = childrenItemViewLabels[childrenItemView]

      const space: Space = {
        ...oldLayer.space,
        delimitationTitle: 'Demarcação por polígono',
        delimitationType: 'custom',
        delimitationCustom: newPolygonDemarcation,
        childrenType,
        childrenItemView,
        childrenTitle,
      }

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const delimitationOptions = useCallback(
    async (
      inputValue: string,
    ): Promise<
      Array<{
        id: SpaceGeoStandardId
        name: string
        type: SpaceGeoStandardType
      }>
    > => {
      const { resultPromise } = callAction({
        query: inputValue.trim(),
        limit: 1500,
        include: [],
        isSearch: true,
      })
      const result = await resultPromise
      let items = result.items.filter(isGeoOption)

      if (items.length > 0 && items[0].searchRanking) {
        items = items
          .filter(item => (item.searchRanking ?? 0) > 0)
          .sort((a, b) => Number(b.searchRanking ?? 0) - Number(a.searchRanking ?? 0))
      }

      return items
    },
    [callAction],
  )

  const debouncedLoadOptions = useMemo(
    () => debounce(delimitationOptions, 500),
    [delimitationOptions, 500],
  )

  const compatibleChildrenForCurrentDelimitation =
    compatibleSpaceChildren[
      currentLayer.space.delimitationType as keyof typeof compatibleSpaceChildren
    ] ?? []

  const baseCompatibleChildren = layer.variable.compatibleChildrenTypes
    ? compatibleChildrenForCurrentDelimitation.filter(type =>
        layer.variable.compatibleChildrenTypes!.includes(type),
      )
    : compatibleChildrenForCurrentDelimitation

  const currentCompatibleChildren = baseCompatibleChildren.filter(type =>
    shouldDisplayChildrenOption(currentLayer, type),
  )

  const dedupedCompatibleChildren = useMemo(() => {
    const seen = new Map<SpaceType, SpaceType>()
    const ordered: SpaceType[] = []

    currentCompatibleChildren.forEach(optionType => {
      const coercedType = coerceChildrenType(currentLayer, optionType)
      const existingOption = seen.get(coercedType)

      if (!existingOption) {
        seen.set(coercedType, optionType)
        ordered.push(optionType)
        return
      }

      if (optionType === coercedType && existingOption !== coercedType) {
        const existingIndex = ordered.indexOf(existingOption)
        if (existingIndex !== -1) {
          ordered[existingIndex] = optionType
        }
        seen.set(coercedType, optionType)
      }
    })

    return ordered
  }, [currentCompatibleChildren, currentLayer])

  const handleCancel = () => {
    setCurrentLayer(layer)
    setPolygonDemarcation(null)
    onClose()
  }

  const handleConfirm = () => {
    const coercedLayer = (() => {
      if (
        currentLayer.space.delimitationType === 'country' &&
        currentLayer.space.childrenType === 'item' &&
        (currentLayer.space as SpaceChildrenItem).childrenItemView === 'cluster'
      ) {
        return {
          ...currentLayer,
          space: {
            ...currentLayer.space,
            childrenItemView: 'heatmap',
            childrenTitle: childrenItemViewLabels['heatmap'],
          } as Space,
        }
      }
      return currentLayer
    })()

    onChangeLayer(coercedLayer)
    onClose()
  }

  const onResetDemarcation = () => {
    setPolygonDemarcation(null)
  }
  const formatOptionLabel = (item: {
    id: SpaceGeoStandardId
    name: string
    type: string
    subtitle?: string
  }) => (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '0.2rem 0' }}>
      <span>{item?.name}</span>
      <span style={{ color: '#a5a4a4', fontSize: '0.8rem' }}>
        {spaceTypeSingularLabels[item?.type as SpaceType]}{' '}
        {item?.subtitle ? ' > ' + item?.subtitle : ''}
      </span>
    </div>
  )

  const selectValue =
    currentLayer.space.delimitationType !== 'custom' && currentLayer.space.delimitationId
      ? {
          id: currentLayer.space.delimitationId,
          name: currentLayer.space.delimitationTitle || 'Carregando...',
          type: currentLayer.space.delimitationType,
        }
      : {
          id: -1,
          name: 'Demarcação customizada',
          type: 'custom' as SpaceType,
        }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Handle>
        <Wrapper>
          <div className='top'>
            <div className='input-wrapper'>
              <AsyncSelect
                cacheOptions
                defaultOptions
                value={selectValue}
                loadOptions={debouncedLoadOptions}
                getOptionValue={item => `${item.id}-${item.type}`}
                formatOptionLabel={formatOptionLabel}
                noOptionsMessage={() => 'Sem resultados'}
                loadingMessage={() => 'Carregando...'}
                placeholder='Delimitação'
                onChange={handleSetDelimitationFromSelect}
                styles={{
                  control: baseStyles => ({
                    ...baseStyles,
                    height: '40px',
                    borderRadius: '8px',
                  }),
                }}
              />
            </div>

            <div className='button-wrapper'>
              <button
                onClick={() => setIsOpenDemarcationModal(true)}
                title='Demarcação customizada'
              >
                <div style={{ marginTop: '4px' }}>
                  <DemarcationIcon fill={'#0C2340'} width={22} height={22} />
                </div>
              </button>
            </div>
          </div>

          {isOpenDemarcationModal && (
            <Modal
              isOpen={isOpenDemarcationModal}
              onClose={() => setIsOpenDemarcationModal(false)}
            >
              <ModalSpaceDemarcation
                onCloseModalDemarcation={() => setIsOpenDemarcationModal(false)}
                setPolygonDemarcation={handleSetDelimitationFromCustomPolygon}
                polygonDemarcation={polygonDemarcation}
              />
            </Modal>
          )}

          <div className='children-options'>
            {dedupedCompatibleChildren.length ? (
              dedupedCompatibleChildren.map((key, index) => {
                const coercedType = coerceChildrenType(currentLayer, key as SpaceType)
                const name =
                  coercedType !== currentLayer.space.delimitationType
                    ? spaceTypePluralLabels[coercedType]
                    : spaceTypeSingularLabels[coercedType]
                return (
                  <Fragment key={index}>
                    {(key as SpaceType) === 'item' ? (
                      Object.keys(childrenItemViewLabels)
                        .filter(itemType => itemType !== 'arc')
                        .filter(itemType =>
                          currentLayer.space.delimitationType === 'country'
                            ? itemType !== 'cluster'
                            : true,
                        )
                        .map((childrenItemViewLabelsKey, index) => {
                          return (
                            <React.Fragment key={index}>
                              <article
                                className='children-option'
                                role='button'
                                onClick={() => {
                                  handleSelectChildrenViewOption('item', childrenItemViewLabelsKey)
                                }}
                              >
                                <div className='title'>
                                  {(currentLayer.space as SpaceChildrenItem).childrenItemView ===
                                    childrenItemViewLabelsKey && (
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                  )}
                                  {
                                    childrenItemViewLabels[
                                      childrenItemViewLabelsKey as keyof typeof childrenItemViewLabels
                                    ]
                                  }
                                </div>
                              </article>
                            </React.Fragment>
                          )
                        })
                    ) : (
                      <article
                        key={index}
                        className='children-option'
                        role='button'
                        onClick={() => {
                          handleSelectChildrenOption(key as Exclude<SpaceType, 'custom'>)
                          onResetDemarcation()
                        }}
                      >
                        <div className='title'>
                          {currentLayer.space.childrenType === coercedType && (
                            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
                          )}
                          {name}
                        </div>
                      </article>
                    )}
                  </Fragment>
                )
              })
            ) : (
              <article className='non-clickable'>
                <div className='title'>
                  <AlertIcon fill={colors.darkGray} width={18} height={18} />
                  <span>
                    A visualização, neste nível geográfico, não é compatível com a seleção atual
                  </span>
                </div>
              </article>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              padding: '10px',
              height: '50px',
              justifyContent: 'right',
            }}
          >
            <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleCancel}>
              Cancelar
            </ButtonLinkAsSimpleText>
            <ButtonLink
              onGo={handleConfirm}
              isDisabled={dedupedCompatibleChildren.length > 0 ? false : true}
            >
              Confirmar
            </ButtonLink>
          </div>
        </Wrapper>
      </Handle>
    </Modal>
  )
}
